import React, { FC, useMemo, useEffect } from 'react';
import { conferenceStatus } from '../utils/eventDataFormat';
import { IAgendaConference, IConferenceSpeaker } from '../event/eventInterface';
import { IUserDetail } from '../context/userContext';
import ConferenceStatus from './views/conferenceStatus';
import ConferenceSpeaker from './views/conferenceSpeaker';
import './styles/conference.scss';
import { Typography } from '@getgo/chameleon-web/react';
import classNames from 'classnames';
import { DateUtils } from '../../utils/dateUtils';
import { ConferenceStatusUtils } from '../utils/conferenceStatusUtils';
import RichTextContent from '../utils/richTextContent/richTextContent';
import { useTranslation } from 'react-i18next';

export interface ConferenceProps {
  conference: IAgendaConference;
  handleEventSpeakerChange?: (index: number, speakerId: string) => void;
  userDetail?: IUserDetail;
  eventKey?: string;
  hasJoinedSession?: boolean;
  joinWebinar: (
    conferenceKey: string,
    actionType: 'JOIN_LINK' | 'VIEW_RECORDING',
    eventName: string,
    eventNameStatus: string,
  ) => Promise<void>;
  setSnackbarStatusDetails?: (conference: IAgendaConference) => void;
}

const Conference: FC<ConferenceProps> = ({
  conference,
  handleEventSpeakerChange,
  userDetail,
  eventKey,
  hasJoinedSession,
  joinWebinar,
  setSnackbarStatusDetails,
}) => {
  const { t, i18n } = useTranslation();
  const status = useMemo(() => {
    return conferenceStatus(new Date(conference.start), new Date(conference.end));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conference.start, conference.end, conference]);

  useEffect(() => {
    if (
      status.toLowerCase() === ConferenceStatusUtils.status.ongoing &&
      !hasJoinedSession &&
      conference.conferenceKey
    ) {
      setSnackbarStatusDetails?.(conference);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div
      className={classNames('conference', {
        'ended-session': status?.toLowerCase() === ConferenceStatusUtils.status.finished,
        registered: userDetail,
      })}
    >
      <div className="conference-box">
        <Typography className="inline-block" variant="caption-default-02" color="text-02">
          {DateUtils.formatTimeRange(conference.start, conference.end, i18n.language)}
        </Typography>
        {userDetail && status && (
          <Typography
            variant="caption-default-02"
            color={ConferenceStatusUtils.conferenceStatusClassNameColor[status.toLowerCase()]}
            className={`inline-block conference-time-status ${
              ConferenceStatusUtils.conferenceStatusClassNameColor[status.toLowerCase()]
            }`}
          >
            <span className="conference-status">{t(`conference.status.${status}`)}</span>
          </Typography>
        )}
      </div>
      <Typography className="conference-header-description-text" tag="h1" variant="body-large">
        {conference.title}
      </Typography>
      <Typography
        className={classNames('conference-description-text', 'preserve-line-breaks')}
        variant="body-small"
        color="text-02"
      >
        <RichTextContent>{conference.description}</RichTextContent>
      </Typography>
      <div className="conference-speaker-box">
        <a className="speaker-section-link">
          <div className="speaker-box">
            {conference.speakers?.map((speaker: IConferenceSpeaker) => (
              <ConferenceSpeaker
                key={speaker.id}
                speaker={speaker}
                handleEventSpeakerChange={handleEventSpeakerChange}
              />
            ))}
          </div>
        </a>
        {userDetail && (
          <ConferenceStatus
            status={status}
            joinWebinar={joinWebinar}
            conference={conference}
            hasJoinedSession={hasJoinedSession}
            eventKey={eventKey}
          />
        )}
      </div>
    </div>
  );
};

export default Conference;
